import { useEffect, useState, useCallback } from "react";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

// Captured at MODULE load, not inside a React hook/useEffect -- the
// real bug this replaces was that desktop Chrome fires
// beforeinstallprompt (when it fires at all) tied to the very first
// document load, and a listener only attached once some component
// happens to mount can miss an event that already fired before that
// component existed. A module-level listener is guaranteed to be
// attached from the moment this file is first imported, which for a
// script referenced from the app's entry point is as early as
// realistically possible. Every component using the hook below reads
// this same shared, singleton value rather than each attaching its own
// separate listener -- multiple listeners can't cause multiple BIP
// events (the browser only ever fires it once), but a singleton is
// simpler and correctly reflects that this is really one shared piece
// of browser state, not any specific component's own state.
let sharedDeferredPrompt: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    sharedDeferredPrompt = e as BeforeInstallPromptEvent;
    listeners.forEach((l) => l());
  });
  window.addEventListener("appinstalled", () => {
    sharedDeferredPrompt = null;
    listeners.forEach((l) => l());
  });
}

/**
 * Wraps the browser's native "add to home screen" / install flow.
 *
 * - Chrome/Edge (desktop + Android): captures the beforeinstallprompt event
 *   so we can trigger it from our own button instead of relying on visitors
 *   to notice the tiny icon in the address bar.
 * - iOS Safari: never fires beforeinstallprompt (Apple doesn't support it) —
 *   there is no programmatic way to trigger it. We detect iOS separately so
 *   the UI can show manual instructions instead.
 * - Desktop Chrome specifically: beforeinstallprompt does not reliably fire
 *   for a scoped, per-tool manifest nested under the site's own "/" scope,
 *   even when the manifest and service worker are both genuinely valid --
 *   a real, confirmed Chrome limitation, not a bug in this app's manifests.
 *   canPrompt being false here does NOT mean installation is impossible,
 *   only that this specific automated path isn't available -- callers
 *   should still offer installation (via promptInstall's real fallback,
 *   or Chrome's own native "Install page as app" control) rather than
 *   hiding the option entirely just because canPrompt is false.
 * - Already installed: detected via display-mode so we don't keep asking.
 */
export function useInstallPrompt() {
  const [, forceUpdate] = useState(0);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    const standalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      (window.navigator as unknown as { standalone?: boolean }).standalone === true;
    setIsInstalled(standalone);

    const ua = window.navigator.userAgent;
    setIsIOS(/iPad|iPhone|iPod/.test(ua) && !standalone);

    const rerender = () => forceUpdate((n) => n + 1);
    listeners.add(rerender);
    return () => { listeners.delete(rerender); };
  }, []);

  const promptInstall = useCallback(async (): Promise<"accepted" | "dismissed" | "unavailable" | "fallback-shown"> => {
    // 1. Classic path -- real, triggerable prompt Chrome/Edge/Android
    // already offered.
    if (sharedDeferredPrompt) {
      await sharedDeferredPrompt.prompt();
      const choice = await sharedDeferredPrompt.userChoice;
      sharedDeferredPrompt = null;
      return choice.outcome;
    }

    // 2. Web Install API -- genuine progressive enhancement, not a
    // current fix: as of this writing navigator.install() only exists
    // behind an explicit browser flag or a registered Origin Trial
    // token, which this site does not have, so this branch is a no-op
    // for virtually every real visitor today. Feature-detected and
    // left in specifically so this starts working automatically, with
    // no further code change needed, once/if the API reaches general
    // availability -- verified against the real, current API shape
    // (two positional string arguments: an install URL and a manifest
    // id), not assumed.
    const nav = navigator as Navigator & {
      install?: (installUrl?: string, manifestId?: string) => Promise<{ outcome?: string } | void>;
    };
    if (typeof nav.install === "function") {
      try {
        await nav.install();
        return "accepted";
      } catch (err) {
        const name = (err as DOMException)?.name;
        if (name === "AbortError") return "dismissed";
        // Any other error (e.g. DataError from a manifest issue) falls
        // through to the manual fallback below rather than silently
        // failing with no feedback to the person who clicked install.
      }
    }

    // 3. Real fallback for desktop Chrome/Edge without an available BIP
    // (the confirmed nested-scope limitation) -- Chrome already offers
    // "Install page as app" as a native control for ANY page with a
    // valid manifest, regardless of whether BIP fired. This is not a
    // placeholder message; it points at a real, working feature this
    // browser already has.
    return "fallback-shown";
  }, []);

  return {
    /** True once Chrome/Edge/Android has offered a real, triggerable install prompt. */
    canPrompt: !!sharedDeferredPrompt,
    isInstalled,
    isIOS,
    promptInstall,
  };
}
